import { ExternalLinkIcon, InfoIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { CopyInput } from '@/components/atoms/CopyInput/CopyInput';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIntegrations } from '@/hooks/useIntegrations';
import { selectAccountSlug } from '@/redux/session/selectors';
import { LiveChatIntegration } from '@/shared/types/integration';
import {
  LIVECHAT_APPS_GUIDE_URL,
  LIVECHAT_AUTHORIZATION_URL,
  LIVECHAT_DEVELOPER_CONSOLE_URL,
} from '@/util/constants';
import { LENGTH_L } from '@/util/validation/constants';

import CardBase from '../Components/CardBase';

interface Props {
  integration: LiveChatIntegration;
}

type ConnectForm = { client_id: string; client_secret: string };

const SCOPES = [
  'chats--access:rw',
  'agents-bot--my:rw',
  'webhooks.configuration:rw',
  'webhooks.state:rw',
  'groups--all:ro',
  'tags--groups:rw',
];

const callbackUrl = `${window.location.origin}/www/api/integrations/livechat/callback`;

const createOAuthURL = (slug: string, integration: LiveChatIntegration, clientId: string) => {
  const { desk_id, integration_id } = integration;
  const queryParams = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: callbackUrl,
    state: JSON.stringify({ desk_id, integration_id, slug }),
  });
  return `${LIVECHAT_AUTHORIZATION_URL}?${queryParams}`;
};

const ConnectCard = ({ integration }: Props) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { updateIntegration, updateStatus } = useIntegrations<LiveChatIntegration>(
    integration.desk_id,
    integration.integration_id
  );

  const form = useForm<ConnectForm>({
    defaultValues: {
      client_id: integration.config?.client_id ?? '',
      client_secret: integration.config?.client_secret ?? '',
    },
  });

  const onSubmit = ({ client_id, client_secret }: ConnectForm) => {
    updateIntegration(
      { ...integration, config: { ...integration.config, client_id, client_secret } },
      { onSuccess: () => window.open(createOAuthURL(slug, integration, client_id), '_self') }
    );
  };

  const rules = {
    required: t('common.required_field'),
    maxLength: { value: LENGTH_L, message: t('validation.max_length', { 0: LENGTH_L }) },
  };

  return (
    <>
      <Alert variant="info" role="note">
        <InfoIcon />
        <AlertTitle>{t('integrations.livechat.connect.guide.title')}</AlertTitle>
        <AlertDescription>
          <ol className="list-decimal pl-4">
            <li>{t('integrations.livechat.connect.guide.create_app')}</li>
            <li>{t('integrations.livechat.connect.guide.select_scopes')}</li>
            <li>{t('integrations.livechat.connect.guide.paste_credentials')}</li>
          </ol>
          <div className="mt-2 flex items-center gap-4">
            <Button size="sm" asChild>
              <a href={LIVECHAT_DEVELOPER_CONSOLE_URL} target="_blank" rel="noopener noreferrer">
                {t('integrations.livechat.connect.guide.open_console')}
              </a>
            </Button>
            <Button variant="link-secondary" size="sm" className="gap-1 px-0" asChild>
              <a href={LIVECHAT_APPS_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                {t('common.learn_more')}
                <ExternalLinkIcon className="size-4" />
              </a>
            </Button>
          </div>
        </AlertDescription>
      </Alert>
      <CardBase title={t('common.setup')} titleVariant="secondary">
        <div className="flex flex-col gap-2">
          <Label>{t('integrations.livechat.connect.redirect_uri')}</Label>
          <CopyInput value={callbackUrl} hasTooltip={false} />
          <p className="text-sm text-foreground-muted">
            {t('integrations.livechat.connect.redirect_uri_help')}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm leading-5 font-medium">
            {t('integrations.livechat.connect.scopes')}
          </span>
          <p className="text-sm text-foreground-muted">
            {t('integrations.livechat.connect.scopes_help')}
          </p>
          <ul className="flex flex-wrap gap-2">
            {SCOPES.map((scope) => (
              <li
                key={scope}
                className="flex items-center gap-0.5 rounded-full bg-bg-muted py-0.5 pr-0.5 pl-2"
              >
                <code className="font-mono text-xs">{scope}</code>
                <CopyButton data={scope} buttonSize="icon-xss" iconSize={12} />
              </li>
            ))}
          </ul>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="client_id"
              rules={rules}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('integrations.livechat.connect.client_id')}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder={t('integrations.livechat.connect.client_id_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="client_secret"
              rules={rules}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('integrations.livechat.connect.client_secret')}</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="off"
                      placeholder={t('integrations.livechat.connect.client_secret_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex justify-end">
              <Button type="submit" isLoading={updateStatus === 'pending'}>
                {t('integrations.connect')}
              </Button>
            </div>
          </form>
        </Form>
      </CardBase>
    </>
  );
};

export default ConnectCard;
